import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {Alert, Avatar, Button, Form, Select, Space} from 'antd';
import {connect} from 'react-redux';
import {Collapse} from 'react-collapse';
import {
  StyledPaymentDetails,
  StyledPaymentDetailsCard,
  StyledPaymentDetailsDropdownButton,
} from './StyledPaymentDetails';
import DetailsTable from '../../../../TransactionsPage/DetailsTable';
import CreditCardSummary from '../../../../../CreditCardSummary';
import CardDetails from '../CardDetails';
import IncreaseLimitAlert from '../../../../SubscriptionsPage/IncreaseLimitAlert';
import {EditLineIcon} from '../../../../../../icons';
import {
  cardTypesConstants,
  subscriptionPaymentTypesConstants,
  subscriptionStatusesConstants,
  subscriptionFormFields,
  subscriptionFormValues
} from '../../../../../../constants';
import {
  amountHelpers,
  cardsHelpers,
  CardLimitPeriodTypeOptions,
  subscriptionsHelpers,
  formHelpers,
  objectHelpers,
  textHelpers,
  PaymentFrequencyTypeOptions
} from '../../../../../../utils/helpers';
import {StyledBudgetDetailsFormSpace} from '../BudgetDetails/StyledBudgetDetails';
import EuroInput from '../../../../../EuroInput';
import SpinSmall from '../../../../../SpinSmall';

const {cardLimitFieldName, cardLimitPeriodFieldName} = subscriptionFormFields;
const {defaultCardLimitPeriod} = subscriptionFormValues;

const gObjProp = objectHelpers.getObjProp;

const {Item} = Form;

const {ACTIVE, CARD_ERROR, DELETED, INACTIVE, PENDING, RESTRICTED,
  TERMINATED, TRIAL, UNMANAGED} = subscriptionStatusesConstants;

const {CARD, WIRE} = subscriptionPaymentTypesConstants;

const PaymentDetails = ({
  dispatch,
  cardDetails,
  cardHold,
  cardHoldMessage,
  employee,
  employees,
  isAdmin,
  isEnabledBanking,
  subscriptionCardLimit,

  className,
  edit = true,
  subscription,
  onAddCard,
  onChangePaymentMethod,
  onRecreateCard,
  onUpdateCardData,
  onFormSubmit,
  isOpenAuthModal = false,
  ...rest
}) => {
  const [t] = useTranslation(['main', 'subscriptions']);
  const [isEditMode, setIsEditMode] = useState(false);
  const [form] = Form.useForm();
  const [initialFormValues, setInitialFormValues] = useState({
    [cardLimitFieldName]: '',
    [cardLimitPeriodFieldName]: defaultCardLimitPeriod,
    payment_frequency: undefined
  });
  const [switchProps, setSwitchProps] = useState({checked: false, loading: false});
  const [cardLimitPeriodTypeOptions] = useState(CardLimitPeriodTypeOptions());
  const [ownerOptions, setOwnerOptions] = useState([]);
  const [paymentFrequencyOptions, ] = useState(PaymentFrequencyTypeOptions());

  const gDataProp = (subscription, key, defaultValue) => gObjProp(subscription, key, defaultValue);

  const trans = (key) => t(`subscriptions:${key}`);

  const placeholderT = (key) => trans(`placeholders.${key}`);

  const getCardOwner = (id) => {
    const employee = employees.find(e => e.employee_id === id);
    return employee ? employee : null;
  }

  const paymentType = useMemo(() => gDataProp(subscription, 'payment_type'), [subscription]);
  const cardId = useMemo(() => gDataProp(subscription, 'card_id', null), [subscription]);

  const {
    isEnabledEdit,
    isActive,
    isTrial,
    isPaused,
    isCardError,
    isHoldCard,
    isCardPaymentType,
    isWirePaymentType,
    isTerminatedCard,
    isNeedUpgrade,
    enableChangeCardAction
  } = useMemo(() => {
    const cardId = gDataProp(subscription, 'card_id', null);
    const paymentType = gDataProp(subscription, 'payment_type');
    const statusCode = gDataProp(subscription, 'status');

    const isEnabledEdit = edit && ![DELETED, TERMINATED, RESTRICTED, UNMANAGED].includes(statusCode);
    const isActive = statusCode === ACTIVE;
    const isTrial = statusCode === TRIAL;
    const isPaused = statusCode === INACTIVE;
    const isPending = statusCode === PENDING;
    const isCardError = statusCode === CARD_ERROR;
    const isCardPaymentType = paymentType === CARD;
    const isWirePaymentType = paymentType === WIRE;
    const isHoldCard = cardHold || (cardId === null && isPending);
    const isTerminatedCard = cardsHelpers.getStatus(cardDetails.data) === TERMINATED && isCardPaymentType;
    const isNeedUpgrade = !isCardPaymentType && isEnabledEdit;
    const enableChangeCardAction = isActive && isCardPaymentType;

    return {
      isEnabledEdit,
      isActive,
      isTrial,
      isPaused,
      isCardError,
      isHoldCard,
      isCardPaymentType,
      isWirePaymentType,
      isTerminatedCard,
      isNeedUpgrade,
      enableChangeCardAction
    };
  }, [subscription, edit, cardHold, cardDetails.data]);

  const cardLimit = Form.useWatch(cardLimitFieldName, {form});

  const requiredRules = [{required: true, message: t('validation.fieldIsRequired')}];

  const isCardOwner = useMemo(() => {
    const cardOwnerId = gObjProp(cardDetails.data, 'user_id');
    const employeeId = gObjProp(employee, 'id');
    return employeeId === cardOwnerId;
  }, [employee, cardDetails]);

  const isTooHighLimit = useMemo(() => {
    let isHigh = false;
    if (isEditMode && isCardPaymentType && cardLimit) {
      isHigh = cardsHelpers.isTooHighLimit({
        limit: cardLimit,
        maxLimit: subscriptionCardLimit
      });
    }
    return isHigh;
  }, [isEditMode, isCardPaymentType, cardLimit, subscriptionCardLimit]);

  const displayProvideAccessMessage = useMemo(() => {
    return !isCardOwner && cardDetails.data;
  }, [isCardOwner, cardDetails]);

  const disabledSaveButton = useMemo(() => isTooHighLimit, [isTooHighLimit]);

  const wireLimits = useMemo(() => ({
    total: subscription?.custom_limit,
    used: subscriptionsHelpers.getSubscriptionTotalPayment(subscription)
  }), [subscription]);

  useEffect(() => {
    const ownerId = gDataProp(cardDetails.data, 'user_id');
    const owner = getCardOwner(ownerId);
    const {
      option: cardLimitPeriod,
      value: cardLimit
    } = cardsHelpers.getCardLimit(cardDetails.data);
    const fieldValues = {
      ...initialFormValues,
      [cardLimitFieldName]: cardLimit,
      [cardLimitPeriodFieldName]: cardLimitPeriod,
      card_owner: owner ? owner.email : ownerId,
      payment_frequency: subscription?.payment_frequency
    };
    setInitialFormValues(fieldValues);
    form.setFieldsValue(fieldValues);
    isEditMode && setIsEditMode(false);
  }, [subscription, cardDetails]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const options = formHelpers.getEmployeeOptions({employees, employeeEmail: employee.email, t});
    setOwnerOptions(options);
  }, [employees]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSwitchProps({
      checked: gDataProp(subscription, 'status') === subscriptionStatusesConstants.ACTIVE,
      loading: false
    });
  }, [cardDetails, subscription]);

  useEffect(() => {
    if (!isOpenAuthModal && switchProps.loading) setSwitchProps({...switchProps, loading: false});
  }, [isOpenAuthModal]); // eslint-disable-line react-hooks/exhaustive-deps

  const cardOwnerAvatarData = useMemo(() => {
    const cardOwner = getCardOwner(gDataProp(cardDetails.data, 'user_id'));

    return {
      label: gObjProp(cardOwner, 'full_name'),
      src: gObjProp(cardOwner, 'logo') || undefined
    }
  }, [cardDetails.data, employees]); // eslint-disable-line react-hooks/exhaustive-deps

  const isOpenedDetailsTable = useMemo(() => isEditMode ? false : !isEditMode && isCardPaymentType ? (!isTerminatedCard && !isHoldCard) : true, [isEditMode, isCardPaymentType, isTerminatedCard, isHoldCard]);

  const isKnownPaymentType = useMemo(() => !objectHelpers.isNaV(paymentType) && paymentType !== '', [paymentType]);

  const error = useMemo(() => isCardPaymentType && cardId === null && isCardError, [isCardPaymentType, cardId, isCardError]);

  const creditCardProps = useMemo(() => ({
    error,
    errorMessage: cardHoldMessage,
    isOnHold: isHoldCard,
    variant: cardTypesConstants.SUBSCRIPTION
  }), [cardHoldMessage, error, isHoldCard]);

  const tooltip = (key) => subscriptionsHelpers.getLabelTooltip({key, translation: t});

  const getTableData = () => {
    let items = [];
    if (isWirePaymentType || isCardPaymentType) {
      items = [
        ...items,
        {
          key: 'paymentFrequency',
          label: trans('paymentFrequency'),
          value: t(subscriptionsHelpers.getPaymentFrequencyType(subscription?.payment_frequency))
        },
      ];
    }
    if (isCardPaymentType) {
      items = [
        ...items,
        {
          key: 'limit',
          label: `${t('card')} ${t('limit')}`,
          value: (
            <>
              {amountHelpers.getAmountWithCurrencyCode(initialFormValues[cardLimitFieldName])}&nbsp;
              {t(subscriptionsHelpers.getCardLimitType(initialFormValues[cardLimitPeriodFieldName]))}
            </>
          )
        },
        {
          key: 'owner',
          label: `${t('card')} ${t('Owner')}`,
          value: (
            <Space>
              {cardOwnerAvatarData.label}
              <Avatar src={cardOwnerAvatarData.src}>
                {textHelpers.getInitials(cardOwnerAvatarData.label)}
              </Avatar>
            </Space>
          )
        },
      ];
    } else if (!isWirePaymentType) {
      let paymentTypeLabel = isKnownPaymentType && t(`subscriptionPaymentTypes.${paymentType}`);
      items = [
        {
          key: 'method',
          label: t('method'),
          value: paymentTypeLabel
        }
      ];
    }
    return items;
  }

  const handleDropdownClick = ({key}) => {
    const events = {
      'edit': () => enableChangeCardAction && setIsEditMode(true),
      'change-method': () => onChangePaymentMethod?.(),
    }
    if (events.hasOwnProperty(key)) events[key]();
  }

  const handleOnCancel = () => setIsEditMode(false);

  const handleOnSave = () => !disabledSaveButton && form.submit();

  const handleSubmit = async (fields) => {
    let data = formHelpers.getUpdatedFormValues({initialValues: initialFormValues, submittedValues: fields});
    let limitFields = [cardLimitFieldName, cardLimitPeriodFieldName];
    // add both limit field if any of them was changed
    if (limitFields.some(key => Object.keys(data).includes(key))) {
      limitFields.forEach(key => {
        data = {...data, [key]: fields[key]}
      });
    }

    if (Object.keys(data).length > 0 && onFormSubmit) {
      onFormSubmit(
        data,
        handleOnCancel
      );
    } else {
      handleOnCancel();
    }
  }

  const tableData = getTableData();

  const extra = (!error && isEnabledEdit) && (
    <>
      {isEditMode ? (
        <Space size='middle'>
          <Button
            onClick={handleOnCancel}
          >
            {t('cancel')}
          </Button>
          <Button
            disabled={disabledSaveButton}
            onClick={handleOnSave}
            type='primary'
          >
            {t('save')}
          </Button>
        </Space>
      ) : (
        <StyledPaymentDetailsDropdownButton
          size='small'
          buttonsRender={([_, rightButton]) => [
            null,
            rightButton,
          ]}
          icon={<EditLineIcon />}
          menu={{
            items: [
              {
                key: 'edit',
                label: t('edit'),
                disabled: (isEditMode || !enableChangeCardAction)
              },
              {
                key: 'change-method',
                label: t('changeMethod')
              },
            ],
            onClick: handleDropdownClick
          }}
          type='text'
        />
      )}
    </>
  );

  const warningAlert = (errorKey) => (
    <Alert
      message={trans(`errors.${errorKey}`)}
      type='warning'
      showIcon
    />
  );

  const recreateCardButton = (
    <Button
      onClick={onRecreateCard}
      size='large'
      type='primary'
    >
      {t('tryAgain')}
    </Button>
  );

  const getCardActions = () => {
    let actions = [];
    if (isActive || isTrial) {
      if (isNeedUpgrade) {
        actions = [
          <Button
            onClick={onChangePaymentMethod}
            size='large'
          >
            {t('changeMethod')}
          </Button>
        ];
      }
      if (isAdmin && isEnabledBanking && !isCardPaymentType) {
        actions.push(
          <Button
            onClick={onAddCard}
            size='large'
            type='primary'
          >
            {t('add')} {t('card')}
          </Button>
        );
      }
      if (error && !isNeedUpgrade) {
        actions.push(recreateCardButton);
      }
    } else if ((isPaused && !isCardPaymentType) || !isEnabledEdit) {
      // Display activate payment type to change payment method if subscription is paused and payment type is not card
      actions.push(warningAlert('activateSubscriptionToChangePaymentMethod'))
    }
    if (isCardPaymentType && isCardError) {
      actions.push(recreateCardButton);
    }
    return actions;
  }

  const handleCardActionSuccess = () => onUpdateCardData?.(true, false)

  return (
    <StyledPaymentDetails
      {...rest}
    >
      <StyledPaymentDetailsCard
        actions={isKnownPaymentType && getCardActions()}
        extra={extra}
        title={t('paymentMethod')}
      >
        {isCardPaymentType && (
          <SpinSmall spinning={cardDetails.loading}>

            {displayProvideAccessMessage && warningAlert('disabledAccessToTheCardDetails')}

            <CardDetails
              className='card-details'
              cardDetails={cardDetails.data}
              cardId={cardDetails?.data?.id}
              creditCardProps={creditCardProps}
              onDeleteSuccess={handleCardActionSuccess}
              onPauseSuccess={handleCardActionSuccess}
            />

          </SpinSmall>
        )}

        {isWirePaymentType && (
          <CreditCardSummary
            className='wire-payment-details'
            limits={wireLimits}
            name='Wire'
          />
        )}

        <Collapse isOpened={isOpenedDetailsTable}>
          <DetailsTable
            data={tableData}
          />
        </Collapse>

        <Collapse isOpened={isEditMode}>
          <Form
            className='pt-20'
            initialValues={initialFormValues}
            form={form}
            layout='vertical'
            onFinish={handleSubmit}
            requiredMark={false}
          >
            <Item
              label={`${t('card')} ${t('Owner')}`}
              name='card_owner'
              required
            >
              <Select
                disabled
                options={ownerOptions}
                size='large'
              />
            </Item>
            <Item
              label={trans('paymentFrequency')}
              name='payment_frequency'
              tooltip={tooltip('paymentFrequency')}
            >
              <Select
                options={paymentFrequencyOptions}
                placeholder={placeholderT('paymentFrequency')}
                size='large'
              />
            </Item>
            <StyledBudgetDetailsFormSpace size='middle'>
              <Item
                label={`${t('card')} ${t('limit')}`}
                name={cardLimitFieldName}
                required
              >
                <EuroInput />
              </Item>
              <Item
                label=' '
                name={cardLimitPeriodFieldName}
                rules={requiredRules}
              >
                <Select
                  options={cardLimitPeriodTypeOptions}
                  size='large'
                />
              </Item>
            </StyledBudgetDetailsFormSpace>
            <Collapse isOpened={isTooHighLimit}>
              <IncreaseLimitAlert
                limit={subscriptionCardLimit}
              />
            </Collapse>
          </Form>
        </Collapse>
      </StyledPaymentDetailsCard>
    </StyledPaymentDetails>
  );
}

PaymentDetails.propTypes = {
  cardDetails: PropTypes.shape({
    loading: PropTypes.bool,
    data: PropTypes.shape({
      loading: PropTypes.bool,
      data: PropTypes.shape({
        id: PropTypes.string,
        user_id: PropTypes.string
      })
    })
  }),
  edit: PropTypes.bool,
  isOpenAuthModal: PropTypes.bool,
  onAddCard: PropTypes.func,
  onChangePaymentMethod: PropTypes.func,
  onRecreateCard: PropTypes.func,
  onUpdateCardData: PropTypes.func,
  onFormSubmit: PropTypes.func,
    subscription: PropTypes.shape({
    payment_frequency: PropTypes.number
  }),
}

const mapStateToProps = state => {
  const {cardHold, cardHoldMessage} = state.subscription;
  const {employee, isAdmin} = state.user;
  const {employees} = state.company;
  const {isEnabledBanking} = state.banking;
  const {subscriptionCardLimit} = cardsHelpers.getPaymentCardLimit(state);
  return {
    cardHold,
    cardHoldMessage,
    employee,
    employees,
    isAdmin,
    isEnabledBanking,
    subscriptionCardLimit
  }
}

export default connect(mapStateToProps, null)(PaymentDetails);
